import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { find } from "lodash"

import { LOADING_INITIAL_STATE, isTimeout, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

// ACTIONS
const requested = createAction("communication/requested")
const received = createAction("communication/received")
const failed = createAction("communication/failed")

const requestedNotificationStatus = createAction("communication/requestedNotificationStatus")
const receivedNotificationStatus = createAction("communication/receivedNotificationStatus")
const failedNotificationStatus = createAction("communication/failedNotificationStatus")

const updatingMessage = createAction("communication/updatingMessage")
const updatedMessage = createAction("communication/updatedMessage")

const updatingContent = createAction("communication/updatingContent")
const updatedContent = createAction("communication/updatedContent")

const fetchingContent = createAction("communication/fetchingContent")
const fetchedContent = createAction("communication/fetchedContent")

const fetchingPreview = createAction("communication/fetchingPreview")
const fetchedPreview = createAction("communication/fetchedPreview")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  marketplace: [],
  manual: [],
  updatingId: null,
  updatingContentId: null,
  fetchingContentId: null,
  contentById: {},
  notificationStatus: {
    loading: false,
    booking_confirmation_email_allowed: true,
    edit_booking_email_allowed: true,
    cancel_booking_email_allowed: true
  },
  fetchingPreviewId: null,
  contentById: {},
  previewById: {}
}

const communicationReducer = createReducer(initialState, {
  [requested.type]: (state, action) => {
    if (isTimeout(state)) { action.abort = true; return }
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    const payload = action.payload?.data || action.payload || {}
    const normalized = Object.entries(payload).flatMap(([category, items]) =>
      Array.isArray(items) ? items.map((item) => ({
        id: item.id,
        section: category.charAt(0).toUpperCase() + category.slice(1),
        type: item.mode,
        name: item.label,
        trigger_event: item.trigger_event,
        enabled: item.enabled,
        disabled: !item.enabled,
        is_editable: !!item.is_editable,
        is_default: item.is_default ?? true,
        subject: item.subject || ""
      })) : []
    )
    state.marketplace = normalized
    state.manual = normalized.map((item) => ({ ...item }))
  },
  [failed.type]: (state) => {
    setFailed(state)
    state.updatingId = null
    state.updatingContentId = null
  },
  [updatingMessage.type]: (state, action) => {
    state.updatingId = action.props.id
  },
  [updatedMessage.type]: (state, action) => {
    setReceived(state)
    state.updatingId = null
    const { id, enabled, is_editable } = action.props
    ;[state.marketplace, state.manual].forEach((list) => {
      const item = find(list, ["id", id])
      if (item) {
        item.enabled = enabled
        item.disabled = !enabled
        if (is_editable !== undefined) item.is_editable = is_editable
      }
    })
  },
  [updatingContent.type]: (state, action) => {
    state.updatingContentId = action.props.id
  },
  [updatedContent.type]: (state, action) => {
    state.updatingContentId = null
    const { id, message_body, footer, body } = action.props
    ;[state.marketplace, state.manual].forEach((list) => {
      const item = find(list, ["id", id])
      if (item) {
        item.message_body = message_body
        item.footer = footer
        if (body) item.body = body
      }
    })
  },
  [fetchingContent.type]: (state, action) => {
    state.fetchingContentId = action.props.id
  },
  [fetchedContent.type]: (state, action) => {
    state.fetchingContentId = null
   const { id, message_body, footer, available_tokens, is_default, default_message, default_footer } = action.props
    state.contentById[id] = { message_body, footer, available_tokens, is_default, default_message, default_footer }
  },
  [requestedNotificationStatus.type]: (state) => {
    state.notificationStatus.loading = true
  },
  [fetchingPreview.type]: (state, action) => {
    state.fetchingPreviewId = action.props.id
  },
  [receivedNotificationStatus.type]: (state, action) => {
    state.notificationStatus.loading = false
    const data = action.payload?.data || action.payload || {}
    state.notificationStatus.booking_confirmation_email_allowed = data.booking_confirmation_email_allowed ?? true
    state.notificationStatus.edit_booking_email_allowed = data.edit_booking_email_allowed ?? true
    state.notificationStatus.cancel_booking_email_allowed = data.cancel_booking_email_allowed ?? true
  },
  [failedNotificationStatus.type]: (state) => {
    state.notificationStatus.loading = false
  },
    [fetchedPreview.type]: (state, action) => {
    state.fetchingPreviewId = null
    const { id, body } = action.props
    state.previewById[id] = { body }
  },
  }
)

export default communicationReducer

// THUNKS
export const fetchCommunicationMessages = (bookingSource) => (dispatch) =>
  dispatch(
    api({
      url: "/guide/settings/communication",
      params: bookingSource ? { booking_source: bookingSource } : undefined,
      onStart: requested.type,
      onSuccess: received.type,
      onError: failed.type
    })
  )

export const updateManualMessage = (id, enabled) => async (dispatch) => {
  dispatch({ type: updatingMessage.type, props: { id } })
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}`,
        method: "patch",
        data: { communication: { enabled } },
        onError: failed.type,
        showSuccessNotification: true
      })
    )
    const payload = data?.data || data || {}
    dispatch({
      type: updatedMessage.type,
      props: {
        id,
        enabled: payload.enabled ?? enabled,
        is_editable: payload.is_editable
      }
    })
  } catch {
    // onError already dispatched by api helper
  }
}

const sanitizeHtml = (val) => {
  const stripped = (val || "").replace(/<br\s*\/?>/gi, "").replace(/<[^>]+>/g, "").trim()
  return stripped ? val : ""
}

export const updateTemplateContent = (id, message_body, footer, isDefault = false) => async (dispatch) => {
  dispatch({ type: updatingContent.type, props: { id } })
  const cleanBody = sanitizeHtml(message_body)
  const cleanFooter = sanitizeHtml(footer)
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}/update_content`,
        method: "patch",
        data: { communication: { message_body: cleanBody, footer: cleanFooter, is_default: isDefault } },
        onError: failed.type,
        showSuccessNotification: true
      })
    )
    const payload = data?.data || {}
    dispatch({
      type: updatedContent.type,
      props: {
        id,
        message_body: payload.message_body ?? message_body,
        footer: payload.footer ?? footer,
        body: payload.body
      }
    })
    if (payload.is_default !== undefined) {
      dispatch({ type: fetchedContent.type, props: { id, message_body: payload.message_body ?? cleanBody, footer: payload.footer ?? cleanFooter, available_tokens: payload.available_tokens ?? [], is_default: payload.is_default, default_message: payload.default_message ?? "", default_footer: payload.default_footer ?? "" } })
    }
  } catch {
    // onError already dispatched by api helper
  }
}

export const resetTemplateContent = (id) => async (dispatch) => {
  dispatch({ type: updatingContent.type, props: { id } })
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}/update_content`,
        method: "patch",
        data: { communication: { message_body: "", footer: "", is_default: true } },
        onError: failed.type,
        showSuccessNotification: true
      })
    )
    const payload = data?.data || {}
    dispatch({
      type: updatedContent.type,
      props: {
        id,
        message_body: payload.message_body ?? "",
        footer: payload.footer ?? "",
        body: payload.body
      }
    })
    dispatch({ type: fetchedContent.type, props: { id, message_body: payload.message_body ?? "", footer: payload.footer ?? "", available_tokens: payload.available_tokens ?? [], is_default: payload.is_default ?? true, default_message: payload.default_message ?? "", default_footer: payload.default_footer ?? "" } })
  } catch {
    // onError already dispatched by api helper
  }
}

export const fetchTemplateContent = (id, bookingSource) => async (dispatch) => {
  dispatch({ type: fetchingContent.type, props: { id } })
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}/content`,
        params: bookingSource ? { booking_source: bookingSource } : undefined,
        onError: failed.type
      })
    )
    const payload = data?.communication || data?.data || data || {}
    dispatch({
      type: fetchedContent.type,
      props: {
        id,
        message_body: payload.message_body || "",
        footer: payload.footer || "",
        available_tokens: payload.available_tokens || [],
        is_default: payload.is_default ?? false,
        default_message: payload.default_message || "",
        default_footer: payload.default_footer || ""
      }
    })
  } catch {
    // onError already dispatched by api helper
  }
}

export const fetchCommunicationNotificationStatus = () => (dispatch) =>
  dispatch(
    api({
      url: "/guides/communication_notification_status",
      onStart: requestedNotificationStatus.type,
      onSuccess: receivedNotificationStatus.type,
      onError: failedNotificationStatus.type
    })
  )
export const fetchCommunicationPreview = (id, bookingSource) => async (dispatch) => {
  dispatch({ type: fetchingPreview.type, props: { id } })
  try {
    const data = await dispatch(
      api({
        url: `/guide/settings/communication/${id}`,
        params: bookingSource ? { booking_source: bookingSource } : undefined,
        onError: failed.type
      })
    )
    const payload = data?.communication || data?.data || data || {}
    dispatch({ type: fetchedPreview.type, props: { id, body: payload.body || "" } })
  } catch {
    // onError already dispatched by api helper
  }
}

// SELECTORS
export const communicationSelector = (state) => state.communication
export const marketplaceMessagesSelector = (state) => state.communication.marketplace
export const manualMessagesSelector = (state) => state.communication.manual
export const communicationLoadingSelector = (state) => state.communication.loading
export const communicationUpdatingIdSelector = (state) => state.communication.updatingId
export const communicationUpdatingContentIdSelector = (state) => state.communication.updatingContentId
export const communicationFetchingContentIdSelector = (state) => state.communication.fetchingContentId
export const communicationContentByIdSelector = (state) => state.communication.contentById
export const communicationNotificationStatusSelector = (state) => state.communication.notificationStatus
export const communicationPreviewByIdSelector = (state) => state.communication.previewById
export const communicationFetchingPreviewIdSelector = (state) => state.communication.fetchingPreviewId
